import React, { useState } from "react";
import { Table, Select } from "antd";
import { useSelector } from "react-redux";
import { useProjectState } from "../../../context/stateContext";

const { Option } = Select;

const SalesInfoTable = () => {
  const { items, total, loading } = useSelector(
    (state) => state.salesInfoReducer
  );

  const {
    dataProducts,
    setDataProducts,
    allSectedProducts,
    setAllSelectedProducts,
    viewMode,
  } = useProjectState();

  const [sortedInfo, setSortedInfo] = useState({});

  const getTotalSum = () => {
    let turnoverSum = 0;
    if (dataProducts.length === 0) return total;
    else {
      dataProducts.forEach((element) => {
        turnoverSum += element.sum;
      });
      return turnoverSum;
    }
  };

  const getGroupedData = (data) => {
    const grouped = {};
    data.forEach((item) => {
      const categoryName = item.categoryName || 'Без група';
      if (!grouped[categoryName]) {
        grouped[categoryName] = {
          name: categoryName,
          count: 0,
          sum: 0,
          categoryId: item.categoryId,
          items: [],
        };
      }
      grouped[categoryName].count += parseFloat(item.count) || 0;
      grouped[categoryName].sum += parseFloat(item.sum) || 0;
      grouped[categoryName].items.push(item);
    });
    return Object.values(grouped);
  };

  const columns = [
    {
      title: "Име",
      dataIndex: "name",
      rowKey: "name",
      width: "50%",
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
      key: "name",
      render: (text) => <span className="sales-info-table-text">{text}</span>,
    },
    {
      title: "Брой",
      dataIndex: "count",
      rowKey: "count",
      width: "10%",
      sorter: (a, b) => a.count - b.count,
      sortOrder: sortedInfo.columnKey === 'count' ? sortedInfo.order : null,
      align: "center",
      key: "count",

      render: (text) => <span className="sales-info-table-text">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="sales-info-table-total-column">
            Общо{" "}
            {getTotalSum()
              .toFixed(2)
              .toString()
              .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")
              .replace(",", " ")}
          </div>
        );
      },
      dataIndex: "sum",
      rowKey: "sum",
      key: "sum",
      align: "right",
      sorter: (a, b) => a.sum - b.sum,
      sortOrder: sortedInfo.columnKey === 'sum' ? sortedInfo.order : null,
      width: "25%",
      render: (text) => (
        <span className="sales-info-table-text">
          {(isNaN(parseFloat(text)?.toFixed(2))
            ? parseFloat(0).toFixed(2)
            : parseFloat(text)?.toFixed(2)) === 0
            ? "-"
            : isNaN(parseFloat(text)?.toFixed(2))
            ? parseFloat(0).toFixed(2)
            : parseFloat(text)?.toFixed(2)}
        </span>
      ),
    },
  ];

  const getSortedItems = (itemsArray) => {
    if (!sortedInfo.columnKey || !sortedInfo.order) return itemsArray;
    
    const sorted = [...itemsArray].sort((a, b) => {
      if (sortedInfo.columnKey === 'name') {
        return sortedInfo.order === 'ascend' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortedInfo.columnKey === 'count') {
        return sortedInfo.order === 'ascend' 
          ? a.count - b.count
          : b.count - a.count;
      }
      if (sortedInfo.columnKey === 'sum') {
        return sortedInfo.order === 'ascend' 
          ? a.sum - b.sum
          : b.sum - a.sum;
      }
      return 0;
    });
    
    return sorted;
  };

  const expandedColumns = [
    {
      title: "Име",
      dataIndex: "name",
      rowKey: "name",
      width: "50%",
      key: "name",
      render: (text) => <span className="sales-info-table-text">{text}</span>,
    },
    {
      title: "Брой",
      dataIndex: "count",
      rowKey: "count",
      width: "10%",
      align: "center",
      key: "count",
      render: (text) => <span className="sales-info-table-text">{text}</span>,
    },
    {
      title: "Общо",
      dataIndex: "sum",
      rowKey: "sum",
      key: "sum",
      align: "right",
      width: "25%",
      render: (text) => (
        <span className="sales-info-table-text">
          {(isNaN(parseFloat(text)?.toFixed(2))
            ? parseFloat(0).toFixed(2)
            : parseFloat(text)?.toFixed(2)) === 0
            ? "-"
            : isNaN(parseFloat(text)?.toFixed(2))
            ? parseFloat(0).toFixed(2)
            : parseFloat(text)?.toFixed(2)}
        </span>
      ),
    },
  ];

  const handleOnSelectProduct = (product) => {
    const selectedProduct = items.filter((item) => {
      return item.name === product;
    });

    setAllSelectedProducts((prevSelectedProducts) => [
      ...prevSelectedProducts,
      product,
    ]);

    setDataProducts((prevSelectedProducts) => [
      ...prevSelectedProducts,
      selectedProduct[0],
    ]);
  };

  const handleDeSelectProduct = (productToRemove) => {
    const unselectedProduct = items.filter((item) => {
      return item.name === productToRemove;
    });

    setAllSelectedProducts((prevSelectedProducts) =>
      prevSelectedProducts.filter((product) => product !== productToRemove)
    );

    setDataProducts((prevSelectedProducts) =>
      prevSelectedProducts.filter((product) => product !== unselectedProduct[0])
    );
  };

  return (
    <div className="sales-info-table-container">
      <Select
        virtual={false}
        key="salesInfoTableSelect"
        mode="multiple"
        placeholder="Филтрирайте по продукт"
        onSelect={handleOnSelectProduct}
        onDeselect={handleDeSelectProduct}
        value={allSectedProducts}
        className="sales-info-table-selector"
      >
        {items?.map((item, index) => (
          <Option key={index} value={item.name}>
            {item.name}
          </Option>
        ))}
      </Select>
      <Table
        loading={loading}
        rowKey={(record) => `${record.name}_${record.categoryId || record.id}`}
        showHeader
        dataSource={
          viewMode === 'groups'
            ? getGroupedData(dataProducts.length === 0 ? items : dataProducts)
            : (dataProducts.length === 0 ? items : dataProducts)
        }
        columns={columns}
        pagination={false}
        className="sales-info-table"
        onChange={(pagination, filters, sorter) => {
          setSortedInfo(sorter);
        }}
        expandable={
          viewMode === 'groups'
            ? {
                expandedRowRender: (record) => {
                  return (
                    <div style={{ margin: 0, paddingLeft: '48px' }}>
                      <Table
                        dataSource={getSortedItems(record.items)}
                        columns={expandedColumns}
                        pagination={false}
                        showHeader={false}
                        rowKey={(item) => `${item.name}_${item.id}`}
                      />
                    </div>
                  );
                },
                rowExpandable: (record) => record.items && record.items.length > 0,
              }
            : undefined
        }
      />
    </div>
  );
};

export default SalesInfoTable;
